<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Table from 'dashboard/components/table/Table.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const columnHelper = createColumnHelper();

const formatMinutes = minutes => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatDateTime = datetime => {
  if (!datetime) return '-';
  const date = new Date(datetime);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const activeLabel = computed(() => t('ATTRIBUTION_REPORTS.ACTIVE'));

const tableData = computed(() => {
  return props.data.map(row => ({
    id: row.id,
    agentName: row.agent_name,
    conversationId: row.conversation_id,
    customerName: row.customer_name,
    startAt: formatDateTime(row.start_at),
    endAt: row.is_active ? activeLabel.value : formatDateTime(row.end_at),
    duration: row.is_active
      ? activeLabel.value
      : formatMinutes(row.duration_minutes),
  }));
});

const columns = computed(() => [
  columnHelper.accessor('agentName', {
    header: t('ATTRIBUTION_REPORTS.TABLE.AGENT'),
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'font-medium text-slate-900 dark:text-slate-100' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('customerName', {
    header: t('ATTRIBUTION_REPORTS.TABLE.CONTACT'),
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('startAt', {
    header: t('ATTRIBUTION_REPORTS.TABLE.START'),
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('endAt', {
    header: t('ATTRIBUTION_REPORTS.TABLE.END'),
    width: 150,
    cell: cellProps => {
      const value = cellProps.getValue();
      const isActive = value === activeLabel.value;
      return h(
        'span',
        {
          class: isActive
            ? 'text-green-600 dark:text-green-400 font-medium'
            : 'text-slate-700 dark:text-slate-300',
        },
        value
      );
    },
  }),
  columnHelper.accessor('duration', {
    header: t('ATTRIBUTION_REPORTS.TABLE.DURATION'),
    align: 'center',
    width: 120,
    cell: cellProps => {
      const value = cellProps.getValue();
      const isActive = value === activeLabel.value;
      return h(
        'span',
        {
          class: isActive
            ? 'text-green-600 dark:text-green-400 font-medium'
            : 'text-slate-700 dark:text-slate-300',
        },
        value
      );
    },
  }),
  columnHelper.accessor('conversationId', {
    header: t('ATTRIBUTION_REPORTS.TABLE.CONVERSATION'),
    align: 'center',
    width: 80,
    cell: cellProps => {
      const conversationId = cellProps.getValue();
      const url = frontendURL(
        `accounts/${accountId.value}/conversations/${conversationId}`
      );
      return h(
        'a',
        {
          href: url,
          class:
            'inline-flex items-center justify-center text-woot-500 dark:text-woot-400 hover:text-woot-600 dark:hover:text-woot-300 transition-colors',
          target: '_blank',
          rel: 'noopener noreferrer',
          title: t('ATTRIBUTION_REPORTS.OPEN_CONVERSATION', {
            id: conversationId,
          }),
        },
        [
          h(FluentIcon, {
            icon: 'open',
            size: 18,
          }),
        ]
      );
    },
  }),
]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});
</script>

<template>
  <div>
    <Table
      v-if="!isLoading && data.length > 0"
      :table="table"
      :is-loading="isLoading"
    />
    <div
      v-else-if="!isLoading && data.length === 0"
      class="flex items-center justify-center p-8"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('ATTRIBUTION_REPORTS.NO_DETAILED_DATA') }}
      </p>
    </div>
    <div v-else-if="isLoading" class="flex items-center justify-center p-8">
      <span class="text-slate-600 dark:text-slate-400">
        {{ $t('ATTRIBUTION_REPORTS.LOADING_DETAILED') }}
      </span>
    </div>
  </div>
</template>
